import { PlusCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { App, Modal } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { type SpecimenCreateInput, SpecimenGroup } from '../../../../graphql/generated/graphql.ts';
import type { Form, Specimen, TestTube } from '../Specimens.tsx';
import { CREATE_SPECIMEN_MUTATION } from './CreateSpecimenModal.gql';
import { SpecimenForm } from './SpecimenForm.tsx';

export const CreateSpecimenModal: React.FC<{
  labId: string;
  specimens: Specimen[];
  testTubes: TestTube[];
  forms: Form[];
  analysisStations: { id: string; name: string }[];
  modalVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ labId, specimens, testTubes, forms, analysisStations, modalVisible, onClose, onSuccess }) => {
  const [createSpecimenMutation, { loading }] = useMutation(CREATE_SPECIMEN_MUTATION);
  const [form] = useForm<SpecimenCreateInput>();
  const { message } = App.useApp();

  const create = async (values: SpecimenCreateInput) => {
    try {
      await createSpecimenMutation({
        variables: {
          input: {
            ...values,
            labId: labId,
          },
        },
      });

      message.success('Material wurde erstellt');
      onSuccess();
      closeAndReset();
    } catch (_e) {
      message.error('Material konnte nicht erstellt werden');
    }
  };

  const closeAndReset = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      width={768}
      title="Neues Material erstellen"
      okText="Erstellen"
      okButtonProps={{ icon: <PlusCircleOutlined /> }}
      open={modalVisible}
      onCancel={closeAndReset}
      onOk={form.submit}
      confirmLoading={loading}
      destroyOnHidden
    >
      <SpecimenForm
        form={form}
        initialValues={{
          labId: labId,
          name: '',
          classification: '',
          analysisStationIds: [],
          testTubeIds: [],
          formIds: [],
          group: SpecimenGroup.NONE,
          features: [],
          storagePeriod: 0,
          requiresLocalizations: false,
          localizationInputEnabled: false,
          predefinedLocalizations: [],
          localizationFields: [],
        }}
        onFinish={values => create(values as SpecimenCreateInput)}
        nameValidator={name => specimens.length === 0 || !specimens.some(s => s.name === name)}
        testTubes={testTubes}
        forms={forms}
        analysisStations={analysisStations}
      />
    </Modal>
  );
};
